import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Pagination,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { ApiRequest } from 'GlobalFunctions';

interface ClientSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelect: (client: any) => void;
  setLoading: (loading: boolean) => void;
}

export default function ClientSelector({ open, onClose, onSelect, setLoading }: ClientSelectorProps) {
  const [list, setList] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtersValues, setFiltersValues] = useState<any>({});
  const [zones, setZones] = useState<any[]>([]);
  const [selectedZone, setSelectedZone] = useState("");

  // Load zones list
  useEffect(() => {
    if (open) {
      loadZones();
    }
  }, [open]);

  const loadZones = async () => {
    await ApiRequest({
      url: "/purchases/zones/list",
      method: "get",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      setResponse: (response: any) => {
        setZones(response.list || []);
      },
    });
  };

  useEffect(() => {
    if (open) {
      loadList();
    }
  }, [page, filtersValues, open]);

  // Add debounced search functionality
  useEffect(() => {
    if (!open) return;
    
    const timeoutId = setTimeout(() => {
      const newFilters = { ...filtersValues };
      
      if (searchTerm.trim() !== '') {
        newFilters.search = { value: searchTerm, label: "Cliente" };
      } else {
        delete newFilters.search;
      }
      
      setFiltersValues(newFilters);
      setPage(1); // Reset to first page on new search
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, open]);

  // Handle zone filter change
  const handleZoneChange = (zoneId: string) => {
    setSelectedZone(zoneId);
    const newFilters = { ...filtersValues };
    
    if (zoneId) {
      newFilters.zone = { value: zoneId, label: "Zona" };
    } else {
      delete newFilters.zone;
    }
    
    setFiltersValues(newFilters);
    setPage(1);
  };

  const loadList = async () => {
    setLoading(true);

    await ApiRequest({
      url: "/purchases/clients/list",
      method: "get",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      query: {
        page: page,
        filters: filtersValues,
      },
      setResponse: (response: any) => {
        setList(response.list);
        setTotalPages(response.total_pages);
        setLoading(false);
      },
    });
  };

  const handleSelect = (client: any) => {
    // Clear filters and search
    setSearchTerm("");
    setSelectedZone("");
    setFiltersValues({});
    setPage(1);
    
    // Select client and close
    onSelect(client);
    onClose();
  };

  const handleClose = () => {
    // Clear filters and search when closing
    setSearchTerm("");
    setSelectedZone("");
    setFiltersValues({});
    setPage(1);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Seleccionar Cliente</span>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', marginTop: '10px' }}>
          <TextField
            sx={{ flex: 1 }}
            variant="outlined"
            placeholder="Buscar por código, NIT, nombre o razón social..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Zona</InputLabel>
            <Select
              value={selectedZone}
              label="Zona"
              onChange={(e) => handleZoneChange(e.target.value as string)}
            >
              <MenuItem value="">Todas las zonas</MenuItem>
              {zones.map((zone: any) => (
                <MenuItem key={zone.zone_id} value={zone.zone_id}>
                  {zone.zone_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <TableContainer>
          <Table aria-label="clients table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: '#a8a8a8', borderBottom: "none" }}>CLIENTE</TableCell>
                <TableCell sx={{ color: '#a8a8a8', borderBottom: "none" }}>UBICACIÓN</TableCell>
                <TableCell sx={{ color: '#a8a8a8', borderBottom: "none" }}>NIT</TableCell>
                <TableCell sx={{ color: '#a8a8a8', borderBottom: "none" }}>ZONA</TableCell>
                <TableCell sx={{ color: '#a8a8a8', borderBottom: "none" }}>ASESOR</TableCell>
                <TableCell sx={{ color: '#a8a8a8', borderBottom: "none" }}>RUTA</TableCell>
                <TableCell sx={{ color: '#a8a8a8', borderBottom: "none" }}>ORDEN</TableCell>
                <TableCell sx={{ color: '#a8a8a8', borderBottom: "none" }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((item, i) => (
                <TableRow key={i} hover role="checkbox">
                  <TableCell>
                    <span className='flex_title'>{item.business_name}</span>
                    <span className='flex_subtitle'>
                      {item.client_name} {item.client_name2} {item.client_last_name} {item.client_last_name2}
                    </span>
                  </TableCell>

                  <TableCell>
                    <span className='flex_title'>{item.neighborhood_name || item.neighborhood}</span>
                    <span className='flex_subtitle'>
                      {item.client_address}
                    </span>
                  </TableCell>

                  <TableCell>
                    {item.document}
                  </TableCell>

                  <TableCell>
                    {item.zone_name || '-'}
                  </TableCell>

                  <TableCell>
                    {item.asesor_name || '-'}
                  </TableCell>

                  <TableCell>
                    {item.visit_route || '-'}
                  </TableCell>

                  <TableCell>
                    {item.route_order || '-'}
                  </TableCell>

                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => handleSelect(item)}
                    >
                      Seleccionar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {list.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            No se encontraron clientes que coincidan con la búsqueda
          </div>
        )}

        <Pagination
          count={totalPages}
          page={page}
          onChange={(event, value) => setPage(value)}
          color="primary"
          style={{ marginTop: "20px", display: 'flex', justifyContent: 'center' }}
        />
      </DialogContent>
    </Dialog>
  );
}
